import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowRight, FlaskConical } from "lucide-react";
import { fetchSampleFile, findSample, readError } from "../lib/api";

const COPY = {
  1: { label: "Try it with a sample PDF", hint: "A document with 73 hidden characters in it" },
  2: { label: "Try it with a sample pair", hint: "An original document and a padded copy of it" },
};

/**
 * Loads the bundled sample documents into a page's dropzones.
 *
 * Sits at the top of the input panel rather than in its header: someone landing
 * on the page looks at the dropzones and the run button, so a quiet header
 * control went unnoticed. Tinted and solid so it stands out without reading as
 * a third dropzone or competing with the primary action below it.
 *
 * The samples come back as real `File` objects, so a sample run takes exactly
 * the same path as an upload. Failures go to the page's existing error alert.
 */
function TrySample({ need = 1, onLoaded, onError, disabled = false }) {
  const [busy, setBusy] = useState(false);
  const controller = useRef(null);
  const { label, hint } = COPY[need] ?? COPY[1];

  useEffect(() => () => controller.current?.abort(), []);

  const load = useCallback(async () => {
    setBusy(true);
    controller.current = new AbortController();
    const { signal } = controller.current;

    try {
      // Two files means an original and the padded copy of it, in that order.
      const wanted =
        need === 2 ? [findSample("clean"), findSample("injected")] : [findSample("injected")];
      const files = await Promise.all(wanted.map((sample) => fetchSampleFile(sample, signal)));
      onError?.(null);
      onLoaded(files);
    } catch (err) {
      if (err?.name === "AbortError" || err?.code === "ERR_CANCELED") return;
      onError?.(readError(err));
    } finally {
      setBusy(false);
    }
  }, [need, onLoaded, onError]);

  return (
    <button type="button" className="trysample" onClick={load} disabled={disabled || busy}>
      <span className="trysample__icon" aria-hidden="true">
        {busy ? <span className="stage__spin" /> : <FlaskConical size={14} />}
      </span>
      <span className="grow">
        {busy ? "Loading the sample…" : label}
        <span className="trysample__hint">{hint}</span>
      </span>
      {!busy && <ArrowRight size={14} aria-hidden="true" className="trysample__go" />}
    </button>
  );
}

export default TrySample;
